import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  lighten,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TablePagination
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import _ from 'lodash';
import { useTranslation, Trans } from 'react-i18next';

import { addTagsToContacts, getCampaignTags, getContacts, getManualTags } from 'API/contact-management';
import { getAllFunnels, updateFunnel } from 'API/linkedin-funnel';
import { Funnel, LinkedInCrawlerContact } from 'features/co-pilot/co-pilot.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import useErrorStore from 'stores/error-store';
import PaiSuccessAlert from 'features/ui/base-components/pai-success-alert';

import {
  Contact,
  Filter,
  FilterColumns,
  FilterLogics,
  FilterOperatorsDate,
  FilterOperatorsNumeric,
  FilterOperatorsText,
  FilterTypes,
  ManualTag,
  filterColumnTypes
} from '../contact-management.types';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';

import AddContactsToCampaignDialog from './add-contacts-to-campaign-dialog';
import AddManualTagsDialog from './add-manual-tags-dialog';
import ContactFilterDrawer from './contact-filter-drawer';
import ContactItem from './contact-item';

interface AddContactsToCampaignDialogState {
  open: boolean;
  loading: boolean;
  selectedFunnel: string | null;
}

interface AddManualTagsDialogState {
  open: boolean;
  loading: boolean;
  selectedTags: OptionType[];
}

const ContactOverview: React.FC = () => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);

  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [addContactsToCampaignDialog, setAddContactsToCampaignDialog] = useState<AddContactsToCampaignDialogState>({
    open: false,
    loading: false,
    selectedFunnel: null
  });
  const [addManualTagsDialog, setAddManualTagsDialog] = useState<AddManualTagsDialogState>({
    open: false,
    loading: false,
    selectedTags: []
  });
  const [manualTags, setManualTags] = useState<ManualTag[]>([]);
  const [campaignTags, setCampaignTags] = useState<string[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [filterLogic, setFilterLogic] = useState<FilterLogics>(FilterLogics.AND);
  const [filters, setFilters] = useState<Filter[]>([
    { column: FilterColumns.CREATE_DATE, operator: FilterOperatorsDate.IS, value: new Date() }
  ]);

  const [successMessage, setSuccessMessage] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  // Setup filter options
  const columns: OptionType[] = _.map(FilterColumns, (col) => ({ label: t(_.camelCase(col)), value: col }));
  const textOperators: OptionType[] = _.map(FilterOperatorsText, (col) => ({ label: t(_.camelCase(col)), value: col }));
  const numericOperators: OptionType[] = _.map(FilterOperatorsNumeric, (col) => ({
    label:
      col === FilterOperatorsNumeric.IS_EMPTY ||
        col === FilterOperatorsNumeric.IS_NOT_EMPTY ||
        col === FilterOperatorsNumeric.IS_ANY_OF
        ? t(_.camelCase(col))
        : col,
    value: col
  }));
  const dateOperators: OptionType[] = _.map(FilterOperatorsDate, (col) => ({ label: t(_.camelCase(col)), value: col }));

  const filterStringSeparator = ';';
  const filterKeySeparator = '::';
  const filterValueSeparator = ',';

  const filterLogicFromUrl = (searchParams.get('logic') || undefined) as FilterLogics | undefined;
  const filtersFromUrl = useMemo(() => revertFilters(searchParams.get('filter')), [
    searchParams,
    manualTags,
    campaignTags,
    filtersOpen
  ]);

  useEffect(() => {
    fetchManualTags();
    fetchCampaignTags();
    fetchFunnels();
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [location]);

  // Debounced search across name + LinkedIn URL. Skip the first render so we
  // don't double-fetch alongside the location effect above.
  const didMountSearchRef = useRef(false);
  useEffect(() => {
    if (!didMountSearchRef.current) {
      didMountSearchRef.current = true;
      return;
    }
    const handle = setTimeout(() => {
      setPage(0);
      fetchContacts(0, rowsPerPage, undefined, undefined, search);
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const fetchFunnels = async () => {
    const res = await getAllFunnels();
    if (!res.error) {
      setFunnels(res.campaigns || []);
    } else {
      setFunnels([]);
      handleOpenErrorDialog(res.error, '/campaigns', 'get');
    }
  };

  const fetchManualTags = async () => {
    const res = await getManualTags();
    if (!res.error) {
      setManualTags(res.tags || []);
    } else {
      handleOpenErrorDialog(res.error, '/contact-management/manual-tags', 'get');
    }
  };

  const fetchCampaignTags = async () => {
    const res = await getCampaignTags();
    if (!res.error) {
      setCampaignTags(res.tags || []);
    } else {
      handleOpenErrorDialog(res.error, '/contact-management/contacts/campaign-tags', 'get');
    }
  };

  /**
   * Fetch contacts from the server
   */
  const fetchContacts = async (
    newPage: number = page,
    newRowsPerPage: number = rowsPerPage,
    newLogic?: string,
    newFilters?: string,
    newSearch: string = search
  ) => {
    const logic = newLogic || searchParams.get('logic') || undefined;
    const filters = newFilters || searchParams.get('filter') || undefined;
    const searchTerm = newSearch.trim() || undefined;

    setContactsLoading(true);

    const res = await getContacts(newPage, newRowsPerPage, logic, filters, searchTerm);
    if (!res.error) {
      setContacts(res.response || []);
      setTotal(res.total || 0);
    } else {
      handleOpenErrorDialog(res.error, '/contact-management/contacts', 'get');
    }

    setContactsLoading(false);
  };

  /**
   * Pagination & selection
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchContacts(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchContacts(0, newRowsPerPage);
  };

  const handleSelectAll = () => {
    setSelectedContacts((prev) => {
      if (prev.length === contacts.length) {
        return [];
      } else {
        return contacts.map((c) => c._id);
      }
    });
  };

  const handleSelect = (contactId: string) => {
    return () => {
      setSelectedContacts((prev) => {
        if (prev.includes(contactId)) {
          return prev.filter((id) => id !== contactId);
        } else {
          return [...prev, contactId];
        }
      });
    };
  };

  /**
   * Filter logic
   */
  function revertFilters(filterQueryString: string | null) {
    if (_.isNil(filterQueryString)) return;

    const filterStrings = filterQueryString.split(filterStringSeparator);
    const filters: Filter[] = [];

    filterStrings.forEach((filterString) => {
      const [column, operator, value] = filterString.split(filterKeySeparator);

      let reconstructedValue;
      if (operator === FilterOperatorsText.IS_EMPTY || operator === FilterOperatorsText.IS_NOT_EMPTY) {
        reconstructedValue = '';
      } else if (operator === FilterOperatorsText.IS_ANY_OF) {
        if (column === FilterColumns.MANUAL_TAGS) {
          const values = value.split(filterValueSeparator);
          reconstructedValue = values.map((val) => getManualFilterObject(val));
        } else if (column === FilterColumns.CAMPAIGN_TAGS) {
          const values = value.split(filterValueSeparator);
          reconstructedValue = values.map((val) => getCampaignFilterObject(val));
        } else {
          reconstructedValue = value.split(filterValueSeparator);
        }
      } else if (filterColumnTypes[column as FilterColumns] === FilterTypes.DATE) {
        reconstructedValue = new Date(value);
      } else if (operator === FilterOperatorsText.EQUALS && column === FilterColumns.MANUAL_TAGS) {
        reconstructedValue = getManualFilterObject(value);
      } else if (operator === FilterOperatorsText.EQUALS && column === FilterColumns.CAMPAIGN_TAGS) {
        reconstructedValue = getCampaignFilterObject(value);
      } else {
        reconstructedValue = value;
      }

      filters.push({
        column: column as FilterColumns,
        operator: operator as FilterOperatorsText | FilterOperatorsNumeric | FilterOperatorsDate,
        value:
          operator === FilterOperatorsText.IS_ANY_OF
            ? ''
            : (reconstructedValue as string | Date | number | OptionType),
        values:
          operator === FilterOperatorsText.IS_ANY_OF
            ? (reconstructedValue as string[] | OptionType[])
            : undefined
      });
    });

    return filters;
  }

  function getManualFilterObject(tagName: string): OptionType {
    return { label: tagName, value: tagName };
  }

  function getCampaignFilterObject(tagName: string): OptionType {
    return { label: tagName, value: tagName };
  }

  const handleOpenFilterDrawer = () => {
    setFiltersOpen(true);
    if (!_.isNil(filterLogicFromUrl)) {
      setFilterLogic(filterLogicFromUrl);
    }
    if (!_.isNil(filtersFromUrl)) {
      setFilters(filtersFromUrl);
    }
  };

  const handleCloseFilterDrawer = () => {
    setFiltersOpen(false);
    setFilters([
      { column: FilterColumns.CREATE_DATE, operator: FilterOperatorsDate.IS, value: new Date() }
    ]);
    setFilterLogic(FilterLogics.AND);
  };

  const handleFilterLogicChange = (value: string) => {
    return () => {
      setFilterLogic(value as FilterLogics);
    };
  };

  const getOperators = (type: FilterColumns) => {
    switch (filterColumnTypes[type]) {
      case FilterTypes.TEXT:
      case FilterTypes.AUTOCOMPLETE:
        return textOperators;
      case FilterTypes.NUMERIC:
        return numericOperators;
      case FilterTypes.DATE:
        return dateOperators;
      default:
        return textOperators;
    }
  };

  const hasSameFilterType = (oldColumn: FilterColumns, newColumn: FilterColumns) => {
    return filterColumnTypes[oldColumn] === filterColumnTypes[newColumn];
  };

  const handleSelectFilterColumn = (idx: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const oldColumn = filters[idx].column;
      setFilters((prev) => {
        const updatedFilters = [...prev];
        const newColumn = event.target.value as FilterColumns;
        updatedFilters[idx].column = newColumn;

        if (!hasSameFilterType(oldColumn, newColumn)) {
          if (filterColumnTypes[newColumn] === FilterTypes.TEXT) {
            updatedFilters[idx].operator = FilterOperatorsText.EQUALS;
            updatedFilters[idx].value = '';
          }
          if (filterColumnTypes[newColumn] === FilterTypes.NUMERIC) {
            updatedFilters[idx].operator = FilterOperatorsNumeric.EQUAL;
            updatedFilters[idx].value = '';
          }
          if (filterColumnTypes[newColumn] === FilterTypes.DATE) {
            updatedFilters[idx].operator = FilterOperatorsDate.IS;
            updatedFilters[idx].value = new Date();
          }
          if (filterColumnTypes[newColumn] === FilterTypes.AUTOCOMPLETE) {
            if (
              event.target.value === FilterColumns.MANUAL_TAGS ||
              event.target.value === FilterColumns.CAMPAIGN_TAGS
            ) {
              updatedFilters[idx].operator = FilterOperatorsText.EQUALS;
              updatedFilters[idx].value = { label: '', value: '' };
            } else {
              updatedFilters[idx].operator = FilterOperatorsText.EQUALS;
              updatedFilters[idx].value = '';
            }
          }
        } else if (newColumn === FilterColumns.CAMPAIGN_TAGS || newColumn === FilterColumns.MANUAL_TAGS) {
          updatedFilters[idx].operator = FilterOperatorsText.EQUALS;
          updatedFilters[idx].value = { label: '', value: '' };
          if (updatedFilters[idx].values) {
            delete updatedFilters[idx].values;
          }
        }

        return updatedFilters;
      });
    };
  };

  const handleSelectFilterOperator = (idx: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilters((prev) => {
        const updatedFilters = [...prev];
        updatedFilters[idx].operator = event.target.value as any;

        if (
          updatedFilters[idx].column === FilterColumns.MANUAL_TAGS ||
          updatedFilters[idx].column === FilterColumns.CAMPAIGN_TAGS
        ) {
          if (event.target.value === FilterOperatorsText.EQUALS) {
            updatedFilters[idx].value = { label: '', value: '' };
            if (updatedFilters[idx].values) {
              delete updatedFilters[idx].values;
            }
          } else if (event.target.value === FilterOperatorsText.IS_ANY_OF) {
            updatedFilters[idx].values = [];
            updatedFilters[idx].value = '';
          } else {
            updatedFilters[idx].value = '';
            if (updatedFilters[idx].values) {
              delete updatedFilters[idx].values;
            }
          }
        } else if (event.target.value === FilterOperatorsText.IS_ANY_OF) {
          updatedFilters[idx].values = [];
          updatedFilters[idx].value = '';
        } else {
          if (updatedFilters[idx].values) {
            delete updatedFilters[idx].values;
          }
        }

        return updatedFilters;
      });
    };
  };

  const handleFilterValueChange = (idx: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilters((prev) => {
        const updatedFilters = [...prev];
        updatedFilters[idx].value = event.target.value;
        return updatedFilters;
      });
    };
  };

  const handleFilterMultiInputChange = (idx: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
      setFilters((prev) => {
        const updatedFilters = [...prev];
        updatedFilters[idx].value = newValue;
        return updatedFilters;
      });
    };
  };

  const handleFilterMultiInputFreeSoloChange = (idx: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>, newValue: string[]) => {
      setTimeout(() => {
        setFilters((prev) => {
          const updatedFilters = [...prev];
          updatedFilters[idx].values = newValue;
          return updatedFilters;
        });
      });
    };
  };

  const handleFilterDateChange = (idx: number) => {
    return (newValue: Date) => {
      setFilters((prev) => {
        const updatedFilters = [...prev];
        updatedFilters[idx].value = newValue;
        return updatedFilters;
      });
    };
  };

  const handleFilterSelectChange = (idx: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>, newValue: OptionType) => {
      setFilters((prev) => {
        const updatedFilters = [...prev];
        updatedFilters[idx].value = newValue;
        return updatedFilters;
      });
    };
  };

  const addFilter = () => {
    setFilters((prev) => {
      const updatedFilters = [...prev];
      updatedFilters.push({
        column: FilterColumns.CREATE_DATE,
        operator: FilterOperatorsDate.IS,
        value: new Date()
      });
      return updatedFilters;
    });
  };

  const removeFilter = (idx: number) => {
    return () => {
      setFilters((prev) => {
        const updatedFilters = [...prev];
        updatedFilters.splice(idx, 1);

        if (updatedFilters.length === 1) {
          setFilterLogic(FilterLogics.AND);
        }

        return updatedFilters;
      });
    };
  };

  const removeAllFilters = () => {
    setFilters([
      { column: FilterColumns.CREATE_DATE, operator: FilterOperatorsDate.IS, value: new Date() }
    ]);
    if (filterLogic === FilterLogics.OR) {
      setFilterLogic(FilterLogics.AND);
    }
    setSearchParams({});
  };

  const handleApplyFilters = () => {
    const filterArray: string[] = [];

    filters.forEach((filter) => {
      filterArray.push(
        `${filter.column}${filterKeySeparator}${filter.operator}${filterKeySeparator}${filter.operator === FilterOperatorsText.IS_EMPTY || filter.operator === FilterOperatorsText.IS_NOT_EMPTY
          ? ''
          : filter.operator === FilterOperatorsText.IS_ANY_OF
            ? typeof filter.values?.[0] === 'string'
              ? (filter.values as string[])?.join(filterValueSeparator)
              : (filter.values as OptionType[])?.map((item) => item.value).join(filterValueSeparator)
            : filterColumnTypes[filter.column] === FilterTypes.DATE
              ? new Date(filter.value as string).toISOString()
              : typeof filter.value === 'string' || typeof filter.value === 'number'
                ? filter.value
                : (filter.value as OptionType).value
        }`
      );
    });

    const filterQuery = filterArray.join(filterStringSeparator);

    setSearchParams({ logic: filterLogic, filter: filterQuery });
    handleCloseFilterDrawer();
  };

  /**
   * Add contacts to campaign dialog
   */
  const handleOpenAddContactsToCampaignDialog = () => {
    setAddContactsToCampaignDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseAddContactsToCampaignDialog = () => {
    setAddContactsToCampaignDialog({ open: false, loading: false, selectedFunnel: null });
  };

  const handleSelectedFunnelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddContactsToCampaignDialog((prev) => ({ ...prev, selectedFunnel: event.target.value }));
  };

  const handleAddContacts = async () => {
    const { selectedFunnel } = addContactsToCampaignDialog;
    if (_.isNil(selectedFunnel)) return;

    setAddContactsToCampaignDialog((prev) => ({ ...prev, loading: true }));

    const selectedFullContacts = contacts.filter((c) => selectedContacts.includes(c._id));
    const mappedContacts = selectedFullContacts.map((c) => {
      const addContact: LinkedInCrawlerContact = { url: '' };

      const { firstname, lastname, profileId, companyName, email } = c;
      if (profileId) addContact.url = profileId;

      if (firstname || lastname) {
        let fullName = firstname ? firstname : '';
        if (lastname) {
          fullName = fullName ? `${fullName} ${lastname}` : lastname;
        }
        addContact.name = fullName;
      }

      if (email) addContact.email = email;
      if (companyName) addContact.currentCompanies = [companyName];

      return addContact;
    });

    const res = await updateFunnel(selectedFunnel, { addContacts: mappedContacts });

    if (!res.error) {
      setSelectedContacts([]);
      handleOpenSuccessMessage(t('contactsSuccessfullyAddedToCampaign'));
      handleCloseAddContactsToCampaignDialog();
    } else {
      setAddContactsToCampaignDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:id', 'patch');
    }

    setTimeout(() => {
      handleCloseAddContactsToCampaignDialog();
    }, 2500);
  };

  /**
   * Add manual tags dialog
   */
  const handleOpenAddManualTagsDialog = () => {
    setAddManualTagsDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseAddManualTagsDialog = () => {
    setAddManualTagsDialog({ open: false, loading: false, selectedTags: [] });
  };

  const handleTagSelectionChange = (event: React.ChangeEvent<HTMLInputElement>, newValue: OptionType[]) => {
    setAddManualTagsDialog((prev) => ({ ...prev, open: true, selectedTags: newValue }));
  };

  const handleAddTags = async () => {
    setAddManualTagsDialog((prev) => ({ ...prev, loading: true }));

    const { selectedTags } = addManualTagsDialog;
    const mappedTags = selectedTags.map((tag) => tag.value);

    const res = await addTagsToContacts(mappedTags, selectedContacts);
    if (!res.error) {
      fetchContacts();
      setSelectedContacts([]);
      handleOpenSuccessMessage(t('tagsSuccessfullyAdded'));
      handleCloseAddManualTagsDialog();
    } else {
      setAddManualTagsDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/contact-management/contacts/add-tags', 'put');
    }
  };

  /**
   * Success message
   */
  const handleOpenSuccessMessage = (message: string) => {
    setSuccessMessage({ open: true, message });
  };

  const handleCloseSuccessMessage = () => {
    setSuccessMessage({ open: false, message: '' });
  };

  return (
    <div className={classes.contactList}>
      {/* Header */}
      <div className={classes.listHeader}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Stack direction={'row'} alignItems={'center'} gap={2}>
            <PaiButton text={t('filter')} action={handleOpenFilterDrawer} />
            <TextField
              size={'small'}
              variant={'outlined'}
              placeholder={t('searchNameOrLinkedInUrl')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: 280 }}
            />
            {!_.isNil(filtersFromUrl) && filtersFromUrl.length > 0 ? (
              <PaiLink
                text={t('removeAllFilters', { count: filtersFromUrl.length })}
                size={'md'}
                action={removeAllFilters}
              />
            ) : null}
          </Stack>
          {selectedContacts.length > 0 && (
            <Stack direction={'row'} gap={2} alignItems={'center'}>
              <Typography className={cx(classes.headerText, 'primary')}>
                {t('selectedContacts', { count: selectedContacts.length })}
              </Typography>
              <PaiLink
                text={t('addToCampaign')}
                size={'md'}
                variant={'primary'}
                action={handleOpenAddContactsToCampaignDialog}
              />
              <PaiLink
                text={t('addTagsToSelection')}
                size={'md'}
                variant={'primary'}
                action={handleOpenAddManualTagsDialog}
              />
            </Stack>
          )}
        </Stack>
      </div>

      {/* Table Content */}
      <div className={classes.listContent}>
        <TableContainer sx={{ width: '100%', height: '100%' }}>
          <Table className={classes.table}>
            {/* Table Head */}
            <TableHead>
              <TableRow className={classes.tableHeadRow}>
                <TableCell>
                  <PaiButton
                    text={selectedContacts.length === contacts.length ? t('unselectAll') : t('selectAll')}
                    size={'small'}
                    border={'round'}
                    action={handleSelectAll}
                  />
                </TableCell>
                <TableCell>{t('name')}</TableCell>
                <TableCell>{t('jobTitle')}</TableCell>
                <TableCell>{t('profileId')}</TableCell>
                <TableCell>{t('connectionDegree')}</TableCell>
                <TableCell>{t('company')}</TableCell>
                <TableCell>{t('email')}</TableCell>
                {/* Removed campaign tags, manual tags, customField columns */}
              </TableRow>
            </TableHead>

            {/* Table Body */}
            <TableBody>
              {contactsLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography className={classes.processingText}>{t('loading')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <ContactItem
                    key={contact._id}
                    contact={contact}
                    selected={selectedContacts.includes(contact._id)}
                    handleSelect={handleSelect(contact._id)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Footer with MUI's TablePagination */}
      <Stack className={classes.listFooter} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        {!_.isEmpty(contacts) ? (
          <Typography className={classes.greyText}>
            <Trans i18nKey="footerNote">
              {t('footerNotePlaceholder')}
            </Trans>
          </Typography>
        ) : (
          <div />
        )}

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('contactsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('of')} ${count}`}
        />
      </Stack>

      {/* Filter Drawer */}
      <ContactFilterDrawer
        open={filtersOpen}
        filterLogic={filterLogic}
        filters={filters}
        filtersLength={filtersFromUrl?.length ?? 0}
        columns={columns}
        manualTags={manualTags}
        campaignTags={campaignTags}
        handleCloseFilterDrawer={handleCloseFilterDrawer}
        handleFilterLogicChange={handleFilterLogicChange}
        removeFilter={removeFilter}
        handleSelectFilterColumn={handleSelectFilterColumn}
        handleSelectFilterOperator={handleSelectFilterOperator}
        handleFilterMultiInputFreeSoloChange={handleFilterMultiInputFreeSoloChange}
        handleFilterMultiInputChange={handleFilterMultiInputChange}
        handleFilterDateChange={handleFilterDateChange}
        handleFilterSelectChange={handleFilterSelectChange}
        handleFilterValueChange={handleFilterValueChange}
        getOperators={getOperators}
        addFilter={addFilter}
        removeAllFilters={removeAllFilters}
        handleApplyFilters={handleApplyFilters}
      />

      {/* Add Contacts to Campaign Dialog */}
      <AddContactsToCampaignDialog
        open={addContactsToCampaignDialog.open}
        loading={addContactsToCampaignDialog.loading}
        funnels={funnels}
        selectedFunnel={addContactsToCampaignDialog.selectedFunnel}
        handleSelectedFunnelChange={handleSelectedFunnelChange}
        handleClose={handleCloseAddContactsToCampaignDialog}
        handleAddContacts={handleAddContacts}
      />

      {/* Add Manual Tags Dialog */}
      <AddManualTagsDialog
        open={addManualTagsDialog.open}
        loading={addManualTagsDialog.loading}
        tags={manualTags}
        selectedTags={addManualTagsDialog.selectedTags}
        handleTagSelectionChange={handleTagSelectionChange}
        handleClose={handleCloseAddManualTagsDialog}
        handleAddTags={handleAddTags}
      />

      {/* Success Alert */}
      <PaiSuccessAlert
        open={successMessage.open}
        message={successMessage.message}
        handleClose={handleCloseSuccessMessage}
      />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  contactList: {
    height: '100%',
    width: '100%',
    borderRadius: '12px',
    background: '#FFF',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  listHeader: {
    padding: '8px 32px',
    borderBottom: `1px solid ${theme.palette.greys.light}`
  },
  listContent: {
    position: 'relative',
    height: '100%',
    padding: '8px',
    overflow: 'auto'
  },
  listFooter: {
    padding: '8px 32px',
    borderTop: `1px solid ${theme.palette.greys.light}`,
    marginTop: 'auto'
  },
  headerText: {
    fontSize: theme.typography.pxToRem(16),
    '&.primary': {
      color: theme.palette.primary.main
    }
  },
  greyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium
  },
  processingText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.light
  },
  tableHeadRow: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFF',
    zIndex: 9
  },
  table: {
    '& th': {
      border: 'none',
      padding: '4px 24px',
      fontSize: theme.typography.pxToRem(13),
      '&:first-of-type': {
        borderTopLeftRadius: '6px',
        borderBottomLeftRadius: '6px'
      },
      '&:last-of-type': {
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px'
      }
    },
    '& td, th': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 24px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
      '& mark': {
        backgroundColor: lighten(theme.palette.secondary.main, 0.8)
      }
    }
  }
}));

export default ContactOverview;
